import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import moment from 'moment';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ContentPasteIcon from '@mui/icons-material/ContentPaste';
import { toast } from 'react-toastify';
import Button from '@/components/CustomButtons/Button';
import Card from '@/components/Card/Card';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import GridContainer from '@/components/Grid/GridContainer';
import GridItem from '@/components/Grid/GridItem';
import CustomInput from '@/components/CustomInput/CustomInput';
import InfoTooltip from '@/components/InfoTooltip/InfoTooltip';
import ConditionsSection from './ConditionsSection';
import CONDITIONS_CONFIG from './conditionsConfig';
import ReportingColumnList from '@/views/Managefile/data-mappings/ReportingColumnList';
import axios from 'axios';
import { API_BASE_URL } from '@/config';
import { ReportTemplatesApi } from '@/api/clients/outbound-reporting/outbound-reporting.api';
import { SftpConnectionsApi } from '@/api/clients/sftp-connections/sftp-connections.api';
import useFormValidation from '@/hooks/useFormValidation';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';

const DELIMITER_OPTIONS = [
  { value: 'comma', label: 'Comma' },
  { value: 'pipe', label: 'Pipe' },
  { value: 'tab', label: 'Tab' },
];
const FILE_TYPE_OPTIONS = ['JSON', 'JSONL', 'Delimited'];
const INTERVAL_OPTIONS = ['Daily', 'Weekly', 'Monthly'];

const DAY_OF_WEEK_OPTIONS = [
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
  { value: '0', label: 'Sunday' },
];

const DAY_OF_MONTH_OPTIONS = Array.from({ length: 31 }, (_, i) => ({
  value: String(i + 1),
  label: String(i + 1),
}));
const DATE_FORMAT_OPTIONS = ['MMDDYYYY', 'YYYYMMDD', 'DDMMYYYY', 'YYYYDDMM'];

function makeCondition() {
  return {
    id: crypto.randomUUID(),
    field: '',
    operator: '',
    value: '',
    logicalOperator: 'AND',
    children: [],
  };
}

const INITIAL_FORM = {
  reporting_name: '',
  file_name: '',
  delimiter: '',
  file_type: '',
  interval: '',
  schedule_time: moment().startOf('day'),
  schedule_day_of_week: '1',
  schedule_day_of_month: '1',
  report_time_range: 'rolling',
  client_sftp_connection_id: '',
  outbound_file_location: '',
  file_extension: '',
  outbound_file_date_format: '',
  encrypt_file: false,
  include_headers_outbound: true,
  sign_file: false,
  signing_pgp_public_key: '',
  pgp_public_key: '',
  data_configuration: [],
  conditions: [],
};

export default function CreateOrEditReporting() {
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [formValues, setFormValues] = useState(INITIAL_FORM);
  const [availableColumns, setAvailableColumns] = useState([]);
  const [availableLineItemColumns, setAvailableLineItemColumns] = useState([]);
  const [sftpConnections, setSftpConnections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [pgpCopied, setPgpCopied] = useState(false);
  const [signingPgpCopied, setSigningPgpCopied] = useState(false);
  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearAllErrors,
  } = useFormValidation();

  useEffect(() => {
    axios
      .get(API_BASE_URL + '/data-mappings/columns-for-reporting')
      .then((res) => {
        const { columnsForReporting, columnsForLineItems } = res.data;
        setAvailableColumns(columnsForReporting);
        setAvailableLineItemColumns(columnsForLineItems);
      })
      .catch(console.error);
  }, []);

  useEffect(() => {
    SftpConnectionsApi.getSftpConnectionsForForm()
      .then((res) => setSftpConnections(res.data || []))
      .catch(console.error);
  }, []);

  useEffect(() => {
    if (!isEditMode) return;
    ReportTemplatesApi.getReportTemplate(id)
      .then((res) => {
        const data = res.data?.data ?? res.data;
        setFormValues({
          ...INITIAL_FORM,
          ...data,
          schedule_time: data.schedule_time
            ? moment(`2000-01-01T${data.schedule_time}`)
            : moment().startOf('day'),
          schedule_day_of_week: data.schedule_day_of_week ?? '1',
          schedule_day_of_month: data.schedule_day_of_month ?? '1',
          data_configuration: data.data_configuration ?? [],
          conditions: data.conditions?.length ? data.conditions : [],
        });
      })
      .catch(console.error);
  }, [id, isEditMode]);

  const handleInputChange = ({ target: { name, value } }) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleAddCondition = () => {
    setFormValues((prev) => ({
      ...prev,
      conditions: [...prev.conditions, makeCondition()],
    }));
  };

  const handleUpdateCondition = (id, field, value) => {
    setFormValues((prev) => ({
      ...prev,
      conditions: prev.conditions.map((c) =>
        c.id === id ? { ...c, [field]: value } : c,
      ),
    }));
  };

  const handleRemoveCondition = (id) => {
    setFormValues((prev) => ({
      ...prev,
      conditions: prev.conditions.filter((c) => c.id !== id),
    }));
  };

  const handleAddDataConfiguration = () => {
    setFormValues((prev) => ({
      ...prev,
      data_configuration: [
        ...prev.data_configuration,
        { id: crypto.randomUUID(), column_name: '', variable_name: '' },
      ],
    }));
  };

  const handleUpdateDataConfiguration = (idx, key, val) => {
    setFormValues((prev) => ({
      ...prev,
      data_configuration: prev.data_configuration.map((col, i) =>
        i === idx ? { ...col, [key]: val } : col,
      ),
    }));
  };

  const handleRemoveDataConfiguration = (idx) => {
    setFormValues((prev) => ({
      ...prev,
      data_configuration: prev.data_configuration.filter((_, i) => i !== idx),
    }));
  };

  const handleCopyDataConfiguration = (e) => {
    e.stopPropagation();
    const json = JSON.stringify({
      _type: 'reporting_data_configuration',
      data: formValues.data_configuration,
    });
    navigator.clipboard.writeText(json).then(() => {
      toast.success(t('reporting.form.configCopied'));
    });
  };

  const handlePasteDataConfiguration = async (e) => {
    e.stopPropagation();
    try {
      const raw = await navigator.clipboard.readText();
      const text = raw
        .replace(/[\u201C\u201D\u201E\u201F\u2033\u2036]/g, '"')
        .replace(/[\u2018\u2019\u201A\u201B\u2032\u2035]/g, "'")
        .trim();
      const parsed = JSON.parse(text);
      if (
        parsed?._type !== 'reporting_data_configuration' ||
        !Array.isArray(parsed.data)
      ) {
        toast.error(t('reporting.form.configPasteInvalid'));
        return;
      }
      setFormValues((prev) => ({
        ...prev,
        data_configuration: parsed.data.map((col) => ({
          ...col,
          id: crypto.randomUUID(),
        })),
      }));
      toast.success(t('reporting.form.configPasted'));
    } catch {
      toast.error(t('reporting.form.configPasteInvalid'));
    }
  };

  const buildPayload = () => ({
    ...formValues,
    schedule_time: formValues.schedule_time?.format('HH:mm') ?? null,
    schedule_day_of_week:
      formValues.interval === 'Weekly' ? formValues.schedule_day_of_week : null,
    schedule_day_of_month:
      formValues.interval === 'Monthly'
        ? formValues.schedule_day_of_month
        : null,
    conditions: formValues.conditions.map((c) => {
      const cfg = CONDITIONS_CONFIG.find((cfg) => cfg.field === c.field);
      return { ...c, type: cfg?.type ?? null };
    }),
  });

  const handleSave = async () => {
    clearAllErrors();
    setSaving(true);
    try {
      const payload = buildPayload();
      if (isEditMode) {
        await ReportTemplatesApi.updateReportTemplate(id, payload);
      } else {
        await ReportTemplatesApi.createReportTemplate(payload);
      }
      navigate(-1);
    } catch (err) {
      setBackendErrors(err.response?.data);
    } finally {
      setSaving(false);
    }
  };

  return (
    <GridContainer>
      <GridItem xs={12}>
        <Card>
          <CardHeader color='primary'>
            <h4>
              {isEditMode
                ? t('reporting.form.editTitle')
                : t('reporting.form.createTitle')}
            </h4>
          </CardHeader>
          <CardBody>
            <FormErrorSummary errors={generalErrors} />
            <GridContainer>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  labelText={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('reporting.form.reportingName')}
                      <InfoTooltip
                        title={t('reporting.form.reportingNameTooltip')}
                        placement='top'
                      />
                    </Box>
                  }
                  id='reporting_name'
                  name='reporting_name'
                  value={formValues.reporting_name}
                  onChange={handleInputChange}
                  formControlProps={{ fullWidth: true }}
                  error={!!fieldErrors.reporting_name}
                  inputProps={{ helperText: fieldErrors.reporting_name }}
                />
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <div className='custom-select'>
                  <FormControl
                    variant='standard'
                    fullWidth
                    error={!!fieldErrors.interval}>
                    <InputLabel id='interval-label'>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {t('reporting.form.interval')}
                        <InfoTooltip
                          title={t('reporting.form.intervalTooltip')}
                          placement='top'
                        />
                      </Box>
                    </InputLabel>
                    <Select
                      labelId='interval-label'
                      name='interval'
                      value={formValues.interval}
                      onChange={handleInputChange}>
                      {INTERVAL_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.interval && (
                      <FormHelperText>{fieldErrors.interval}</FormHelperText>
                    )}
                  </FormControl>
                </div>
              </GridItem>
            </GridContainer>

            {formValues.interval && (
              <GridContainer>
                {formValues.interval === 'Weekly' && (
                  <GridItem xs={12} sm={12} md={6}>
                    <div className='custom-select'>
                      <FormControl variant='standard' fullWidth>
                        <InputLabel id='day-of-week-label'>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('reporting.form.dayOfWeek')}
                            <InfoTooltip
                              title={t('reporting.form.dayOfWeekTooltip')}
                              placement='top'
                            />
                          </Box>
                        </InputLabel>
                        <Select
                          labelId='day-of-week-label'
                          name='schedule_day_of_week'
                          value={formValues.schedule_day_of_week}
                          onChange={handleInputChange}>
                          {DAY_OF_WEEK_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </GridItem>
                )}

                {formValues.interval === 'Monthly' && (
                  <GridItem xs={12} sm={12} md={6}>
                    <div className='custom-select'>
                      <FormControl variant='standard' fullWidth>
                        <InputLabel id='day-of-month-label'>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('reporting.form.dayOfMonth')}
                            <InfoTooltip
                              title={t('reporting.form.dayOfMonthTooltip')}
                              placement='top'
                            />
                          </Box>
                        </InputLabel>
                        <Select
                          labelId='day-of-month-label'
                          name='schedule_day_of_month'
                          value={formValues.schedule_day_of_month}
                          onChange={handleInputChange}>
                          {DAY_OF_MONTH_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                  </GridItem>
                )}

                <GridItem xs={12} sm={12} md={6}>
                  <div className='custom-select'>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <TimePicker
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {t('reporting.form.time')}
                            <InfoTooltip
                              title={t('reporting.form.timeTooltip')}
                              placement='top'
                            />
                          </Box>
                        }
                        value={formValues.schedule_time}
                        onChange={(val) =>
                          handleInputChange({
                            target: { name: 'schedule_time', value: val },
                          })
                        }
                        slotProps={{
                          textField: { variant: 'standard', fullWidth: true },
                        }}
                      />
                    </LocalizationProvider>
                  </div>
                </GridItem>

                <GridItem xs={12} sm={12} md={6}>
                  <FormControl component='fieldset' sx={{ mt: 1 }}>
                    <FormLabel
                      component='legend'
                      sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {t('reporting.form.reportTimeRange')}
                        <InfoTooltip
                          title={t('reporting.form.reportTimeRangeTooltip')}
                          placement='top'
                        />
                      </Box>
                    </FormLabel>
                    <RadioGroup
                      name='report_time_range'
                      value={formValues.report_time_range}
                      onChange={handleInputChange}>
                      <FormControlLabel
                        value='rolling'
                        control={<Radio size='small' />}
                        label={t('reporting.form.reportTimeRange.rolling')}
                      />
                      <FormControlLabel
                        value='calendar'
                        control={<Radio size='small' />}
                        label={t('reporting.form.reportTimeRange.calendarDays')}
                      />
                    </RadioGroup>
                  </FormControl>
                </GridItem>
              </GridContainer>
            )}

            <h5 style={{ margin: '16px 0 8px' }}>
              {t('reporting.form.fileSettings')}
            </h5>
            <GridContainer>
              <GridItem xs={12} sm={12} md={6}>
                <div className='custom-select'>
                  <FormControl
                    variant='standard'
                    fullWidth
                    error={!!fieldErrors.file_type}>
                    <InputLabel id='file-type-label'>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {t('reporting.form.fileType')}
                        <InfoTooltip
                          title={t('reporting.form.fileTypeTooltip')}
                          placement='top'
                        />
                      </Box>
                    </InputLabel>
                    <Select
                      labelId='file-type-label'
                      name='file_type'
                      value={formValues.file_type}
                      onChange={(e) => {
                        handleInputChange(e);
                        if (e.target.value !== 'Delimited') {
                          handleInputChange({
                            target: { name: 'delimiter', value: '' },
                          });
                        }
                      }}>
                      {FILE_TYPE_OPTIONS.map((opt) => (
                        <MenuItem key={opt} value={opt}>
                          {opt}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.file_type && (
                      <FormHelperText>{fieldErrors.file_type}</FormHelperText>
                    )}
                  </FormControl>
                </div>
              </GridItem>

              {formValues.file_type === 'Delimited' && (
                <GridItem xs={12} sm={12} md={6}>
                  <div className='custom-select'>
                    <FormControl
                      variant='standard'
                      fullWidth
                      error={!!fieldErrors.delimiter}>
                      <InputLabel id='delimiter-label'>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {t('reporting.form.delimiter')}
                          <InfoTooltip
                            title={t('reporting.form.delimiterTooltip')}
                            placement='top'
                          />
                        </Box>
                      </InputLabel>
                      <Select
                        labelId='delimiter-label'
                        name='delimiter'
                        value={formValues.delimiter}
                        onChange={handleInputChange}>
                        {DELIMITER_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {fieldErrors.delimiter && (
                        <FormHelperText>{fieldErrors.delimiter}</FormHelperText>
                      )}
                    </FormControl>
                  </div>
                </GridItem>
              )}

              <GridItem xs={12} sm={12} md={6}>
                <div className='custom-select'>
                  <FormControl
                    variant='standard'
                    fullWidth
                    error={!!fieldErrors.client_sftp_connection_id}>
                    <InputLabel id='sftp-connection-label'>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {t('reporting.form.sftpConnection')}
                        <InfoTooltip
                          title={t('reporting.form.sftpConnectionTooltip')}
                          placement='top'
                        />
                      </Box>
                    </InputLabel>
                    <Select
                      labelId='sftp-connection-label'
                      name='client_sftp_connection_id'
                      value={formValues.client_sftp_connection_id}
                      onChange={handleInputChange}>
                      <MenuItem value=''>
                        <em>None</em>
                      </MenuItem>
                      {sftpConnections.map((conn) => (
                        <MenuItem key={conn.id} value={conn.id}>
                          {conn.name || conn.host}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.client_sftp_connection_id && (
                      <FormHelperText>
                        {fieldErrors.client_sftp_connection_id}
                      </FormHelperText>
                    )}
                  </FormControl>
                </div>
              </GridItem>
              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  labelText={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('reporting.form.fileName')}
                      <InfoTooltip
                        title={t('reporting.form.fileNameTooltip')}
                        placement='top'
                      />
                    </Box>
                  }
                  id='file_name'
                  name='file_name'
                  value={formValues.file_name}
                  onChange={handleInputChange}
                  formControlProps={{ fullWidth: true }}
                  error={!!fieldErrors.file_name}
                  inputProps={{ helperText: fieldErrors.file_name }}
                />
              </GridItem>

              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  labelText={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('reporting.form.outboundFileLocation')}
                      <InfoTooltip
                        title={t('reporting.form.outboundFileLocationTooltip')}
                        placement='top'
                      />
                    </Box>
                  }
                  id='outbound_file_location'
                  name='outbound_file_location'
                  value={formValues.outbound_file_location}
                  onChange={handleInputChange}
                  formControlProps={{ fullWidth: true }}
                  error={!!fieldErrors.outbound_file_location}
                  inputProps={{
                    helperText:
                      fieldErrors.outbound_file_location ||
                      t('reporting.form.outboundFileLocationHelper'),
                  }}
                />
              </GridItem>

              <GridItem xs={12} sm={12} md={6}>
                <CustomInput
                  labelText={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('reporting.form.fileExtension')}
                      <InfoTooltip
                        title={t('reporting.form.fileExtensionTooltip')}
                        placement='top'
                      />
                    </Box>
                  }
                  id='file_extension'
                  name='file_extension'
                  value={formValues.file_extension}
                  onChange={handleInputChange}
                  formControlProps={{ fullWidth: true }}
                  error={!!fieldErrors.file_extension}
                  inputProps={{ helperText: fieldErrors.file_extension }}
                />
              </GridItem>

              <GridItem xs={12} sm={12} md={6}>
                <div className='custom-select'>
                  <FormControl
                    variant='standard'
                    fullWidth
                    error={!!fieldErrors.outbound_file_date_format}>
                    <InputLabel id='date-format-label'>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {t('reporting.form.dateFormat')}
                        <InfoTooltip
                          title={t('reporting.form.dateFormatTooltip')}
                          placement='top'
                        />
                      </Box>
                    </InputLabel>
                    <Select
                      labelId='date-format-label'
                      name='outbound_file_date_format'
                      value={formValues.outbound_file_date_format}
                      onChange={handleInputChange}>
                      {DATE_FORMAT_OPTIONS.map((fmt) => (
                        <MenuItem key={fmt} value={fmt}>
                          {fmt}
                        </MenuItem>
                      ))}
                    </Select>
                    {fieldErrors.outbound_file_date_format && (
                      <FormHelperText>
                        {fieldErrors.outbound_file_date_format}
                      </FormHelperText>
                    )}
                  </FormControl>
                </div>
              </GridItem>

              <GridItem xs={12} sm={12} md={6}>
                <FormControl component='fieldset' sx={{ mt: 1 }}>
                  <FormLabel
                    component='legend'
                    sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('reporting.form.encryptFile')}
                      <InfoTooltip
                        title={t('reporting.form.encryptFileTooltip')}
                        placement='top'
                      />
                    </Box>
                  </FormLabel>
                  <RadioGroup
                    row
                    name='encrypt_file'
                    value={String(formValues.encrypt_file)}
                    onChange={(e) =>
                      handleInputChange({
                        target: {
                          name: 'encrypt_file',
                          value: e.target.value === 'true',
                        },
                      })
                    }>
                    <FormControlLabel
                      value='true'
                      control={<Radio size='small' />}
                      label={t('common.yes')}
                    />
                    <FormControlLabel
                      value='false'
                      control={<Radio size='small' />}
                      label={t('common.no')}
                    />
                  </RadioGroup>
                </FormControl>
              </GridItem>

              <GridItem xs={12} sm={12} md={6}>
                <FormControl component='fieldset' sx={{ mt: 1 }}>
                  <FormLabel
                    component='legend'
                    sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('reporting.form.includeHeaders')}
                      <InfoTooltip
                        title={t('reporting.form.includeHeadersTooltip')}
                        placement='top'
                      />
                    </Box>
                  </FormLabel>
                  <RadioGroup
                    row
                    name='include_headers_outbound'
                    value={String(formValues.include_headers_outbound)}
                    onChange={(e) =>
                      handleInputChange({
                        target: {
                          name: 'include_headers_outbound',
                          value: e.target.value === 'true',
                        },
                      })
                    }>
                    <FormControlLabel
                      value='true'
                      control={<Radio size='small' />}
                      label={t('common.yes')}
                    />
                    <FormControlLabel
                      value='false'
                      control={<Radio size='small' />}
                      label={t('common.no')}
                    />
                  </RadioGroup>
                </FormControl>
              </GridItem>

              <GridItem xs={12} sm={12} md={6}>
                <FormControl component='fieldset' sx={{ mt: 1 }}>
                  <FormLabel
                    component='legend'
                    sx={{ fontSize: '0.75rem', mb: 0.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {t('reporting.form.signFile')}
                      <InfoTooltip
                        title={t('reporting.form.signFileTooltip')}
                        placement='top'
                      />
                    </Box>
                  </FormLabel>
                  <RadioGroup
                    row
                    name='sign_file'
                    value={String(formValues.sign_file)}
                    onChange={(e) =>
                      handleInputChange({
                        target: {
                          name: 'sign_file',
                          value: e.target.value === 'true',
                        },
                      })
                    }>
                    <FormControlLabel
                      value='true'
                      control={<Radio size='small' />}
                      label={t('common.yes')}
                    />
                    <FormControlLabel
                      value='false'
                      control={<Radio size='small' />}
                      label={t('common.no')}
                    />
                  </RadioGroup>
                </FormControl>
              </GridItem>

              {isEditMode && formValues.sign_file === true && (
                <GridItem xs={12}>
                  <div style={{ marginBottom: '20px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}>
                      <label
                        htmlFor='signing_pgp_public_key'
                        style={{
                          fontWeight: '400',
                          color: '#333',
                          marginRight: '10px',
                        }}>
                        {t('reporting.form.signingPgpPublicKey')}
                      </label>
                      <InfoTooltip
                        title={t('reporting.form.signingPgpPublicKeyTooltip')}
                        placement='top'
                      />
                      {formValues.signing_pgp_public_key && (
                        <Tooltip
                          title={
                            signingPgpCopied ? 'Copied!' : 'Copy to clipboard'
                          }
                          placement='top'>
                          <IconButton
                            size='small'
                            color='primary'
                            style={{ marginLeft: '6px' }}
                            onClick={() => {
                              navigator.clipboard.writeText(
                                formValues.signing_pgp_public_key,
                              );
                              setSigningPgpCopied(true);
                              setTimeout(
                                () => setSigningPgpCopied(false),
                                2000,
                              );
                            }}>
                            <ContentCopyIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                    <textarea
                      id='signing_pgp_public_key'
                      name='signing_pgp_public_key'
                      value={formValues.signing_pgp_public_key}
                      disabled
                      style={{
                        width: '100%',
                        minHeight: '120px',
                        padding: '12px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        backgroundColor: '#f5f5f5',
                      }}
                    />
                  </div>
                </GridItem>
              )}

              {formValues.encrypt_file === true && (
                <GridItem xs={12}>
                  <div style={{ marginBottom: '20px' }}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        marginBottom: '8px',
                      }}>
                      <label
                        htmlFor='pgp_public_key'
                        style={{
                          fontWeight: '400',
                          color: '#333',
                          marginRight: '10px',
                        }}>
                        {t('reporting.form.pgpPublicKey')}
                      </label>
                      <InfoTooltip
                        title={t('reporting.form.pgpPublicKeyTooltip')}
                        placement='top'
                      />
                      {formValues.pgp_public_key && (
                        <Tooltip
                          title={pgpCopied ? 'Copied!' : 'Copy to clipboard'}
                          placement='top'>
                          <IconButton
                            size='small'
                            color='primary'
                            style={{ marginLeft: '6px' }}
                            onClick={() => {
                              navigator.clipboard.writeText(
                                formValues.pgp_public_key,
                              );
                              setPgpCopied(true);
                              setTimeout(() => setPgpCopied(false), 2000);
                            }}>
                            <ContentCopyIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      )}
                    </div>
                    <textarea
                      id='pgp_public_key'
                      name='pgp_public_key'
                      value={formValues.pgp_public_key}
                      onChange={(e) =>
                        handleInputChange({
                          target: {
                            name: 'pgp_public_key',
                            value: e.target.value,
                          },
                        })
                      }
                      style={{
                        width: '100%',
                        minHeight: '120px',
                        padding: '12px',
                        border: fieldErrors.pgp_public_key
                          ? '1px solid #d32f2f'
                          : '1px solid #ddd',
                        borderRadius: '4px',
                        fontFamily: 'monospace',
                        fontSize: '14px',
                        resize: 'vertical',
                        boxSizing: 'border-box',
                      }}
                    />
                    {fieldErrors.pgp_public_key && (
                      <FormHelperText error>
                        {fieldErrors.pgp_public_key}
                      </FormHelperText>
                    )}
                  </div>
                </GridItem>
              )}
            </GridContainer>

            <Accordion sx={{ mt: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box
                  sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Typography
                    color={
                      fieldErrors.data_configuration ? 'error' : 'inherit'
                    }>
                    {t('reporting.form.dataConfiguration')}
                  </Typography>
                  <InfoTooltip
                    title={t('reporting.form.dataConfigurationTooltip')}
                    placement='top'
                  />
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                    <Tooltip title={t('reporting.form.copyConfig')}>
                      <IconButton
                        size='small'
                        onClick={handleCopyDataConfiguration}
                        disabled={formValues.data_configuration.length === 0}>
                        <ContentCopyIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('reporting.form.pasteConfig')}>
                      <IconButton
                        size='small'
                        onClick={handlePasteDataConfiguration}>
                        <ContentPasteIcon fontSize='small' />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <ReportingColumnList
                  columns={formValues.data_configuration}
                  onUpdateColumn={handleUpdateDataConfiguration}
                  onRemoveColumn={handleRemoveDataConfiguration}
                  onAddColumn={handleAddDataConfiguration}
                  availableColumns={availableColumns}
                  availableLineItemColumns={availableLineItemColumns}
                  columnTypeLabel={t('reporting.form.dataConfiguration')}
                />
              </AccordionDetails>
            </Accordion>
            {fieldErrors.data_configuration && (
              <FormHelperText error sx={{ px: 1 }}>
                {fieldErrors.data_configuration}
              </FormHelperText>
            )}

            <Card>
              <CardHeader>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <h5 style={{ margin: 0 }}>
                    {t('reporting.form.conditions')}
                  </h5>
                  <InfoTooltip
                    title={t('reporting.form.conditionsTooltip')}
                    placement='top'
                  />
                </Box>
              </CardHeader>
              <CardBody>
                <ConditionsSection
                  conditions={formValues.conditions}
                  onAdd={handleAddCondition}
                  onUpdate={handleUpdateCondition}
                  onRemove={handleRemoveCondition}
                  error={fieldErrors.conditions}
                />
              </CardBody>
            </Card>

            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                marginTop: 8,
              }}>
              <Button
                color='primary'
                startIcon={
                  saving ? (
                    <CircularProgress size={16} color='inherit' />
                  ) : (
                    <SaveIcon style={{ color: 'white' }} />
                  )
                }
                onClick={handleSave}
                disabled={saving}>
                {saving ? t('reporting.form.saving') : t('reporting.form.save')}
              </Button>
            </div>
          </CardBody>
        </Card>
      </GridItem>
    </GridContainer>
  );
}
